import React, { Suspense } from 'react';
import type { Organism } from '@verndale/core';
import globModules from './glob-modules';
import { renderElement } from './helpers/react-18-shim';
import { withStore } from './hoc/withStore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ModuleSkeleton from './components/react-components/ModuleSkeleton';

const defaultQueryClient = new QueryClient();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reactModule = (Component: any, nodeList: NodeListOf<HTMLElement>) => {
  const WithStoreComponent = withStore(Component);
  return nodeList.forEach(node => {
    // Get module type from data attribute for skeleton sizing
    const moduleType = node.dataset.module || 'default';
    renderElement(
      <QueryClientProvider client={defaultQueryClient}>
        <Suspense fallback={<ModuleSkeleton type={moduleType} />}>
          <WithStoreComponent {...node.dataset} />
        </Suspense>
      </QueryClientProvider>,
      node
    );
  });
};

const modules: Organism[] = [
  {
    name: 'industryTabs',
    loader: () => import('./modules/industry-tabs')
  },
  {
    name: 'slider',
    loader: () => import('./modules/slider')
  },
  {
    name: 'mockApi',
    styles: () => import('../scss/modules/mock-api.scss'),
    loader: () => import('./modules/mockApi'),
    render: reactModule
  },
  {
    name: 'reactInput',
    loader: () => import('./components/react-components/Input'),
    render: reactModule
  },
  {
    name: 'mockInput',
    loader: () => import('./components/input')
  },
  {
    name: 'sectionFacet',
    loader: () => import('./components/react-components/SectionFacet'),
    render: reactModule
  },
  {
    name: 'selectedFacets',
    loader: () => import('./components/react-components/SelectedFacets'),
    render: reactModule
  },
  {
    name: 'listing',
    loader: () => import('./components/react-components/Listing'),
    render: reactModule
  },
  {
    name: 'header',
    loader: () => import('./modules/header')
  },
  {
    name: 'modal',
    loader: () => import('./components/modal')
  },
  {
    name: 'verticalTabs',
    loader: () => import('./components/verticalTabs')
  },
  {
    name: 'predictiveSearch',
    loader: () => import('./components/react-components/PredictiveSearch'),
    render: reactModule
  },
  {
    name: 'regionLanguageSelector',
    loader: () => import('./components/regionLanguageSelector')
  },
  {
    name: 'languageSelector',
    loader: () => import('./components/languageSelector')
  },
  {
    name: 'accordion',
    loader: () => import('./components/accordion')
  },
  {
    name: 'ourBrands',
    loader: () => import('./components/ourBrands')
  },
  {
    name: 'productSeriesMasthead',
    loader: () => import('./modules/productSeriesMasthead')
  },
  {
    name: 'video',
    loader: () => import('./components/video')
  },
  {
    name: 'productMatchQuiz',
    loader: () => import('./modules/productMatchQuiz')
  },
  {
    name: 'wizardQuiz',
    loader: () => import('./components/react-components/WizardQuiz'),
    render: reactModule
  },
  {
    name: 'specificationsTable',
    loader: () => import('./modules/productSpecificationsTable')
  },
  {
    name: 'productSectionFacet',
    loader: () => import('./components/react-components/ProductSectionFacet'),
    render: reactModule
  },
  {
    name: 'productListing',
    loader: () => import('./components/react-components/ProductListing'),
    render: reactModule
  },
  {
    name: 'form',
    loader: () => import('./modules/form')
  },
  {
    name: 'animatedLine',
    loader: () => import('./components/animatedLine'),
    styles: () => import('../scss/modules/animated-line.scss')
  },
  {
    name: 'contentHighlightImageRow',
    styles: () => import('../scss/modules/_content-highlight-image-row.scss')
  },
  {
    name: 'fullWithMedia',
    styles: () => import('../scss/modules/_full-with-media.scss')
  },
  {
    name: 'generalCardRow',
    styles: () => import('../scss/modules/_general-card-row.scss')
  },
  {
    name: 'sectionSearchListingFacet',
    loader: () => import('./components/react-components/SectionSearchListingFacet'),
    render: reactModule
  },
  {
    name: 'searchListing',
    loader: () => import('./components/react-components/SearchListing'),
    render: reactModule
  },
  {
    name: 'homepageMasthead',
    loader: () => import('./modules/homepageMasthead')
  },
  {
    name: 'productList',
    loader: () => import('./modules/productList')
  },
  {
    name: 'productNotAvailable',
    styles: () => import('../scss/components/_product-not-available.scss')
  },
  {
    name: 'wizardDealerQuiz',
    loader: () => import('./components/react-components/WizardDealerQuiz'),
    render: reactModule
  },
  {
    name: 'dealerMatchQuiz',
    loader: () => import('./modules/dealerMatchQuiz')
  },
  {
    name: 'dealerOverviewCta',
    loader: () => import('./components/dealerOverviewCta')
  }
];

export default [...globModules, ...modules];
